import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, AlertTriangle, Info } from 'lucide-react'

interface BasicsStepProps {
  state: any
  updateState: (updates: any) => void
}

export function BasicsStep({ state, updateState }: BasicsStepProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Basic Information</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Give your alert rule a name and set its severity level
        </p>
      </div>

      <div className="space-y-4">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">
            Rule Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            placeholder="e.g., Speeding Alert"
            value={state.name}
            onChange={(e) => updateState({ name: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Choose a descriptive name that identifies the purpose of this alert
          </p>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="e.g., Alert when any vehicle exceeds speed limit"
            value={state.description}
            onChange={(e) => updateState({ description: e.target.value })}
            rows={3}
          />
        </div>

        {/* Severity */}
        <div className="space-y-2">
          <Label htmlFor="severity">
            Severity <span className="text-destructive">*</span>
          </Label>
          <Select value={state.severity} onValueChange={(value) => updateState({ severity: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="info">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-500" />
                  <span>Info</span>
                  <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-800">
                    Low priority
                  </Badge>
                </div>
              </SelectItem>
              <SelectItem value="warning">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span>Warning</span>
                  <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-800">
                    Medium priority
                  </Badge>
                </div>
              </SelectItem>
              <SelectItem value="critical">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span>Critical</span>
                  <Badge variant="secondary" className="ml-2 bg-red-100 text-red-800">
                    High priority
                  </Badge>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Critical alerts will be highlighted and may trigger immediate notifications
          </p>
        </div>

        {/* Enabled */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-1">
            <Label htmlFor="enabled">Enable rule immediately</Label>
            <p className="text-xs text-muted-foreground">
              Start monitoring and sending alerts as soon as this rule is saved
            </p>
          </div>
          <Switch
            id="enabled"
            checked={state.enabled}
            onCheckedChange={(checked) => updateState({ enabled: checked })}
          />
        </div>
      </div>
    </div>
  )
}
